'use client';

import type { ReactNode } from 'react';

import { ArticleCard } from '@/components/article/article-card';
import type { ArticleCarouselItem } from '@/components/article/article-carousel-item';
import {
  CarouselArrowControls,
  CarouselDots,
  CarouselTrack,
  useCarousel,
} from '@/components/site/carousel';
export type { ArticleCarouselItem } from '@/components/article/article-carousel-item';

type ArticleCarouselProps = {
  items: ArticleCarouselItem[];
  previousLabel: string;
  nextLabel: string;
  className?: string;
  controlsClassName?: string;
  controlsPlacement?: 'top' | 'bottom';
  footerClassName?: string;
  footerContent?: ReactNode;
  trackClassName?: string;
  showControls?: boolean;
  showDots?: boolean;
  dotLabelPrefix?: string;
};

const VISIBLE_COUNT = { base: 1, sm: 2, lg: 4 } as const;

export function ArticleCarousel({
  items,
  previousLabel,
  nextLabel,
  className = 'mt-10',
  controlsClassName = 'mb-6 flex justify-end gap-2',
  controlsPlacement = 'top',
  footerClassName = 'mt-8 flex items-center justify-between gap-4',
  footerContent,
  trackClassName = 'overflow-hidden',
  showControls = true,
  showDots = false,
  dotLabelPrefix = 'Go to articles position',
}: ArticleCarouselProps) {
  const carousel = useCarousel({ itemCount: items.length, visibleCount: VISIBLE_COUNT });
  if (!items.length) return null;

  const controls =
    showControls && carousel.paginated ? (
      <CarouselArrowControls
        previous={carousel.previous}
        next={carousel.next}
        atStart={carousel.atStart}
        atEnd={carousel.atEnd}
        previousLabel={previousLabel}
        nextLabel={nextLabel}
        className={controlsClassName}
      />
    ) : null;

  return (
    <div className={className}>
      {controlsPlacement === 'top' ? controls : null}

      <div className={trackClassName}>
        <CarouselTrack
          items={items}
          getKey={(article) => article.slug}
          current={carousel.current}
          itemWidth={carousel.itemWidth}
          visibleCount={carousel.visibleCount}
          renderItem={({ item, tabIndex }) => (
            <ArticleCard article={item} tabIndex={tabIndex} titleElement="h3" />
          )}
        />
      </div>

      {controlsPlacement === 'bottom' && (controls || footerContent) ? (
        <div className={footerClassName}>
          <div>{controls}</div>
          {footerContent ? <div>{footerContent}</div> : null}
        </div>
      ) : null}

      {showDots && carousel.paginated ? (
        <CarouselDots
          count={carousel.maxPosition + 1}
          current={carousel.current}
          goToPosition={carousel.goToPosition}
          labelPrefix={dotLabelPrefix}
        />
      ) : null}
    </div>
  );
}
